// Shared mutable state for the typing-pile experiment.

export type TypedString = {
  id: string;
  text: string;
  createdAt: number;
  // DB row id. Set once the server insert succeeds (for fresh drops) or on
  // hydration from listJarMessages. Absent for strings that are purely local
  // (e.g. an in-flight drop before the insert resolves).
  messageId?: string;
  // Author's auth.users.id. `null` means "posted anonymously" (uneditable
  // across sessions). `undefined` means we don't know yet.
  authorUserId?: string | null;
  // True if this client dropped it in the current session — used so an
  // anonymous poster can still revive/edit their own note within the session.
  sessionOwned?: boolean;
  // Display name for the note. `null` means "posted as Anonymous". `undefined`
  // means we don't know yet (fresh drops pre-commit).
  senderName?: string | null;
};

export type ReviveState = {
  stringId: string;
  startTime: number;
  /** The longest per-body `startDelayMs` + the shared move duration. Used to detect completion. */
  totalDurationMs: number;
  bodyIds: number[]; // matter body ids being revived
  targets: Map<number, { x: number; y: number; angle: number; startDelayMs: number }>;
  starts: Map<number, { x: number; y: number; angle: number }>;
  // True when the caller can't edit (someone else's note, or anon cross-session).
  // On completion, bodies stay static at the typing-box target positions
  // instead of being removed from the world, so dismissing can animate them back.
  readOnly: boolean;
  // True when this tween is the reverse-dismiss: bodies fly from the
  // typing-box area back into the pile and regain dynamics on completion.
  reverse: boolean;
};

/**
 * Captured while a read-only revive is "held open" — the letters sit static
 * at the typing-box positions while the viewer reads. Stores the original
 * pile positions so dismissing can animate the letters back down.
 */
export type ReadOnlyHold = {
  stringId: string;
  bodyIds: number[];
  pileStarts: Map<number, { x: number; y: number; angle: number }>;
};

export type AppState = {
  strings: Map<string, TypedString>;
  revive: ReviveState | null;
  readOnlyHold: ReadOnlyHold | null;
  lastSpawnTime: number;
};

export const state: AppState = {
  strings: new Map(),
  revive: null,
  readOnlyHold: null,
  lastSpawnTime: 0,
};

export function makeStringId(): string {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}
